/**
 * TorreVivo — el tab "En vivo" de la Torre de control, con SUS datos adentro.
 *
 * Los hooks de datos vivos (useTorreControlData: 5 endpoints cada 90s;
 * useActividadData: poll de paquetes por repartidor + socket GPS) viven ACÁ y
 * no en TorreControl a propósito: TabsContent desmonta el tab inactivo, así
 * que con "Analítica" activa no queda ningún polling ni socket de esta
 * sección corriendo. Volver al tab remonta y refetchea — dato fresco al
 * volver, cero costo mientras no se mira.
 *
 * El estado de capas vive en el padre para sobrevivir al cambio de tab; el
 * popover (layersOpen) es local y se cierra al desmontar, como cualquier menú.
 */
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Layers, RefreshCw } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { Button } from '../../../components/ui/Button';
import { Card } from '../../../components/ui/Card';
import { DateNav } from '../../../components/ui/DateNav';
import { RelativeTime } from '../../../components/ui/RelativeTime';
import useTheme from '../../../hooks/useTheme';
import useActividadData from '../actividad/useActividadData';
import useTorreControlData from './useTorreControlData';
import TorreMap from './TorreMap';
import KpiBand from './components/KpiBand';
import OperationHealth from './components/OperationHealth';
import AlertsPanel from './components/AlertsPanel';
import DriverBoard from './components/DriverBoard';

// Identidad estable mientras rutas === null (loading): un `rutas || []` inline
// crearía un array nuevo por render y derrotaría el memo de TorreMap.
const EMPTY_RUTAS = [];

export default function TorreVivo({ date, today, onDateChange, layers, onToggleLayer }) {
  const { t } = useTranslation();
  const { isDark } = useTheme();
  const [layersOpen, setLayersOpen] = useState(false);

  const isLiveDay = date === today;

  // Datos de dashboard/reportes/incidencias.
  const {
    loading: dashLoading,
    refreshing,
    error,
    lastUpdated,
    refetch,
    kpis,
    pendingBreakdown,
    incidents,
  } = useTorreControlData({ date });

  // Rutas/repartidores + GPS en vivo (mismo adapter que live-routes).
  const { rutas, loading: rutasLoading, gpsConnected, refetch: refetchRutas } = useActividadData({ date });

  const activeDrivers = useMemo(
    () => (rutas || []).filter((r) => r.device?.gps).length,
    [rutas],
  );
  const totalDrivers = (rutas || []).length;

  const handleRefresh = () => {
    refetch();
    refetchRutas();
  };

  // Cierra el popover de capas al hacer clic fuera.
  useEffect(() => {
    if (!layersOpen) return undefined;
    const onClick = (e) => {
      if (!e.target.closest?.('.tc-layers')) setLayersOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [layersOpen]);

  const activeLayerCount = Object.values(layers).filter(Boolean).length;

  const LAYER_DEFS = [
    { id: 'conductores', label: t('Repartidores') },
    { id: 'pendientes', label: t('Pendientes') },
    { id: 'entregados', label: t('Entregados') },
    { id: 'fallidos', label: t('Fallidos') },
  ];

  return (
    <>
      {/* Controles del día + estado en vivo */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 min-w-0">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          {isLiveDay && (
            <span className="relative flex h-2.5 w-2.5" aria-hidden>
              <span className="absolute inline-flex h-full w-full rounded-full bg-status-success opacity-70 motion-safe:animate-ping" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-status-success" />
            </span>
          )}
          <span className="text-sm font-medium text-fg">
            {isLiveDay ? t('Operación en vivo') : date}
          </span>
          {isLiveDay && !gpsConnected && (
            <span className="inline-flex items-center gap-1 text-[11px] text-status-warning">
              <AlertTriangle className="w-3 h-3" /> {t('Sin conexión GPS en vivo.')}
            </span>
          )}
        </div>

        <div className="-mx-4 md:mx-0 px-4 md:px-0 overflow-x-auto md:overflow-visible">
          <div className="flex flex-nowrap items-center gap-2 min-w-max md:min-w-0">
            <DateNav date={date} today={today} onChange={onDateChange} t={t} />
            <Button
              variant="outline"
              size="sm"
              iconLeft={<RefreshCw className={cn('w-3.5 h-3.5', refreshing && 'motion-safe:animate-spin')} />}
              onClick={handleRefresh}
              disabled={refreshing}
            >
              <span className="hidden sm:inline">{t('Refrescar')}</span>
            </Button>
            {lastUpdated && (
              <span className="text-[11px] text-fg-muted whitespace-nowrap hidden sm:inline">
                {t('Actualizado')}{' '}
                <RelativeTime value={lastUpdated} live t={t} />
              </span>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div
          className="flex items-center justify-between gap-3 rounded-lg border border-status-error/30 bg-status-error-soft px-4 py-2.5 text-sm text-status-error"
          role="alert"
        >
          <span>{error}</span>
          <button
            type="button"
            onClick={handleRefresh}
            className="font-medium underline underline-offset-2 hover:no-underline shrink-0"
          >
            {t('Reintentar')}
          </button>
        </div>
      )}

      {/* KPIs */}
      <KpiBand
        kpis={kpis}
        activeDrivers={activeDrivers}
        totalDrivers={totalDrivers}
        loading={dashLoading}
      />

      {/* Mapa + salud */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card padding="none" className="lg:col-span-2 overflow-hidden">
          <div className="relative w-full" style={{ height: 'clamp(360px, 52vh, 560px)' }}>
            <TorreMap isDark={isDark} rutas={rutas || EMPTY_RUTAS} layers={layers} />

            {/* Control de capas */}
            <div className="tc-layers absolute top-3 left-3 z-[400]">
              <button
                type="button"
                onClick={() => setLayersOpen((o) => !o)}
                className="h-9 px-3 inline-flex items-center gap-2 rounded-lg border border-border bg-bg shadow-card text-sm font-medium text-fg hover:bg-bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
                aria-expanded={layersOpen}
              >
                <Layers className="w-4 h-4 text-fg-muted" />
                {t('Capas')}
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-brand-500 text-white text-[10px] font-bold tabular-nums">
                  {activeLayerCount}
                </span>
              </button>
              {layersOpen && (
                <div className="mt-2 w-52 rounded-lg border border-border bg-bg shadow-elevated p-1.5">
                  {LAYER_DEFS.map((l) => (
                    <label
                      key={l.id}
                      className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-bg-muted cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={layers[l.id]}
                        onChange={() => onToggleLayer(l.id)}
                        className="rounded border-border text-brand-500 focus:ring-brand-500"
                      />
                      <span className="text-sm text-fg">{l.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Indicador "en vivo" */}
            {isLiveDay && (
              <div className="absolute top-3 right-3 z-[400]">
                <div className="h-9 px-3 inline-flex items-center gap-2 rounded-lg border border-border bg-bg shadow-card text-xs font-medium">
                  <span className="relative flex h-2 w-2" aria-hidden>
                    <span className="absolute inline-flex h-full w-full rounded-full bg-status-success opacity-70 motion-safe:animate-ping" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-status-success" />
                  </span>
                  <span className="text-fg">{t('En vivo')}</span>
                  <span className="text-fg-muted">·</span>
                  <span className="text-fg-muted">
                    {activeDrivers} {t('en ruta')}
                  </span>
                </div>
              </div>
            )}

            {/* Leyenda */}
            <div className="absolute bottom-3 left-3 z-[400]">
              <div className="rounded-lg border border-border bg-bg/95 shadow-card px-3 py-2">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px] text-fg-muted">
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-status-success" />
                    {t('Repartidor')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-status-success" />
                    {t('Entregada')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-status-warning" />
                    {t('Pendiente')}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <span className="w-2.5 h-2.5 rounded-full bg-status-error" />
                    {t('No entregada')}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <OperationHealth kpis={kpis} pendingBreakdown={pendingBreakdown} loading={dashLoading} />
      </div>

      {/* Repartidores + alertas */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <DriverBoard rutas={rutas} loading={rutasLoading} />
        <AlertsPanel incidents={incidents} loading={dashLoading} />
      </div>
    </>
  );
}
